import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { LoaderFunction, MetaFunction, useParams } from "react-router";
import { useFontStyle } from "~/hook/useFontStyle";
import { cn } from "~/lib/utils";

// Import components
import GyanjivanDasjiSwami from "~/components/static-page-components/GyanjivandasjiSwami";
import OurActivities from "~/components/static-page-components/OurActivities";
import useLocale from "~/hook/useLocale";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { metaService } from "~/utils/metaService";

const NAVIGATION = [
  { enName: "Who We Are", guName: "અમે કોણ છીએ", slug: "who-we-are", component: OurActivities },
  {
    enName: "H.H. Gyanjivandasji Swami",
    guName: " પ.પૂ. સદ શ્રી જ્ઞાનજીવનદાસજી સ્વામી",
    slug: "gyanjivandasji-swami",
    component: GyanjivanDasjiSwami,
  },
];

export const meta: MetaFunction = ({ location, params }) => {
  const lang = params.lang || "en";
  const pathname = location.pathname;
  const meta = getPageMeta(params.slug as any, lang);

  return metaService({
    title: meta.metaTitle,
    description: meta.metaDescription,
    ogTitle: meta.ogTitle,
    ogDescription: meta.ogDescription,
    ogUrl: pathname,
    canonicalLink: pathname,
    hreflangs: meta.hreflangs?.map((lang: any) => ({
      ...lang,
      href: `${lang.href}${pathname}`,
    })),
  });
};

export const loader: LoaderFunction = ({ params }) => {
  const slug = params.slug || "swaminarayan-sampraday";
  return Response.json({ slug });
};

const OurSect = () => {
  const params = useParams();
  const { LocalizedLink } = useLocale();
  const { medium } = useFontStyle();
  const [activeSlug, setActiveSlug] = useState(params.slug || "kundaldham-activity");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const lang = useLanguageParams();

  // Update active slug when route params change
  useEffect(() => {
    if (params.slug) {
      setActiveSlug(params.slug);
      setIsMobileMenuOpen(false); // Close mobile menu on navigation
    }
  }, [params.slug]);

  // Get active component
  const activeNav = NAVIGATION.find((nav) => nav.slug === activeSlug) || NAVIGATION[0];
  const ActiveComponent = activeNav.component;

  const handleNavClick = (slug: string) => {
    setActiveSlug(slug);
    setIsMobileMenuOpen(false);
    // Smooth scroll to top of content
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div>
      <div className="-mt-5 md:-mt-0 mx-auto pb-6 relative">
        {/* Mobile Menu Toggle */}
        <div className="lg:hidden mb-6 px-4">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={cn(
              "w-full flex items-center justify-between px-6 py-4 bg-white rounded-xl shadow-lg border-2 border-primaryColor/20 hover:border-primaryColor/40 transition-all duration-300",
              medium,
            )}
          >
            <span className="text-textColor text-lg">{lang === "gu" ? activeNav.guName : activeNav.enName}</span>
            <motion.svg
              animate={{ rotate: isMobileMenuOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className="w-6 h-6 text-primaryColor"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </motion.svg>
          </button>

          {/* Mobile Navigation Dropdown */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden mt-2"
              >
                <div className="bg-white rounded-xl shadow-xl border-2 border-primaryColor/20 p-2 space-y-1">
                  {NAVIGATION.map((nav, index) => (
                    <motion.div key={nav.slug} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }}>
                      <LocalizedLink
                        to={`/about-us/${nav.slug}`}
                        onClick={() => handleNavClick(nav.slug)}
                        className={cn(
                          "block w-full text-left px-6 py-3 rounded-lg transition-all duration-300",
                          medium,
                          activeSlug === nav.slug
                            ? "bg-primaryColor text-white shadow-lg"
                            : "text-textColor hover:bg-primaryColor/10 hover:text-primaryColor",
                        )}
                      >
                        {lang === "gu" ? nav.guName : nav.enName}
                      </LocalizedLink>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Desktop Layout */}
        <div className="flex gap-6 lg:gap-8">
          {/* Left Navigation Panel - Desktop Only */}
          <motion.aside
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="hidden lg:block lg:sticky lg:top-24 lg:self-start lg:ml-0 max-w-56 flex-shrink-0 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="bg-white rounded-2xl shadow-xl border-2 border-primaryColor/20 overflow-hidden"
            >
              {/* Navigation Header */}
              <div className="bg-buttonColor p-2">
                <h2 className={cn("text-white text-xl font-bold text-center", medium)}>About Us</h2>
              </div>

              {/* Navigation Items */}
              <nav className="p-2 space-y-2">
                {NAVIGATION.map((nav, index) => (
                  <motion.div
                    key={nav.slug}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                  >
                    <LocalizedLink
                      to={`/about-us/${nav.slug}`}
                      onClick={() => handleNavClick(nav.slug)}
                      className={cn(
                        "block w-full text-left px-4 py-2 transition-all duration-300 group relative overflow-hidden",
                        medium,
                        activeSlug === nav.slug
                          ? "border-l-4 border-l-buttonColor text-textHeadingColor"
                          : "text-textColor hover:bg-primaryColor/10 hover:text-primaryColor border-l-4 border-l-transparent",
                      )}
                    >
                      {/* Active indicator line */}
                      {activeSlug === nav.slug && (
                        <motion.div
                          layoutId="activeIndicator"
                          className="absolute left-0 top-0 bottom-0 w-1.5 bg-white rounded-r-full"
                          transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        />
                      )}

                      {/* Navigation text */}
                      <span className="relative z-10 block">{lang === "gu" ? nav.guName : nav.enName}</span>

                      {/* Hover background effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-primaryColor/0 via-primaryColor/5 to-primaryColor/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </LocalizedLink>
                  </motion.div>
                ))}
              </nav>
            </motion.div>
          </motion.aside>

          {/* Right Content Area */}
          <motion.main
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex-1 min-w-0 "
          >
            {/* <div className="bg-white rounded-2xl shadow-xl border-2 border-primaryColor/20 overflow-hidden"> */}
            {/* Content with smooth transition */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSlug}
                //       initial={{ opacity: 0, x: 20 }}
                //       animate={{ opacity: 1, x: 0 }}
                //       exit={{ opacity: 0, x: -20 }}
                //       transition={{ duration: 0.4, ease: "easeInOut" }}
                //         className="p-6 md:p-8 lg:p-12"
              >
                <ActiveComponent />
              </motion.div>
            </AnimatePresence>
            {/* </div> */}
          </motion.main>
        </div>
      </div>
    </div>
  );
};

export default OurSect;
